import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { IMAGE_TYPES } from '../../../../constants';
import { calculateBMI, formatHeight } from '../../../utils/bmi';

const wlusAltOnlyQuestions = [
  {
    field: 'wlusAltOnly_intro',
    label:
      'Before beginning your intake form, please gather the information needed to make your consultation as smooth and safe as possible.',
    subLabel:
      'If you are already taking a weight-loss medication, have your prescription details available so we can confirm the correct medication and dose. If it is unclear, we will ask you to upload a photo of your prescription so we can confirm the dose for you.  Please make sure that everything is accurate to expedite the process. If you are new to these medications, your doctor will select the safest and most appropriate starting dose.\n\nAfter your information is sent to the doctor, you will receive a greeting message—please check your messages regularly so your care team can stay in touch.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [{ value: 'Lets get started', key: 'letsGetStarted' }]
    },
    extras: { sendToBeluga: false }
  },
  {
    field: 'wlusAltOnly_sex',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    },
    extras: { sendToBeluga: false }
  },
  {
    field: 'wlusAltOnly_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        {
          value:
            'No and I acknowledge that these medications are not safe to use during pregnancy or while breastfeeding, as they may pose risks to a developing baby.',
          key: 'no_acknowledge'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_highestWeight',
    label: 'What was the highest weight that you have reached?',
    type: 'info',
    answer: {
      type: 'number',
      name: 'wlusAltOnly_highestWeight'
    }
  },
  {
    label: 'What is your:',
    field: 'wlusAltOnly_weightAndHeight',
    type: 'info',
    required: false,
    answer: {
      type: 'list',
      required: false,
      items: [
        {
          type: 'weightHeight',
          showLabel: true
        },
        {
          type: 'calculated',
          label: 'Your BMI is',
          answer: {
            formula: data => calculateBMI(data)
          }
        },
        {
          type: 'calculated',
          label: 'What is your height in feet and inches?',
          answer: {
            formula: data => formatHeight(data)
          }
        },
        {
          type: 'calculated',
          label: 'What is your weight in pounds?',
          answer: {
            formula: data => {
              const { weight, weightUnit } = data;
              return (weight || '') + ' ' + (weightUnit || '');
            }
          }
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_currentApproach',
    label: 'How would you describe your current approach to weight management?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Actively managing', key: 'actively_managing' },
        { value: 'Some efforts', key: 'some_efforts' },
        { value: 'No active efforts', key: 'no_active_efforts' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_medicalConditionsChecklist',
    label: 'Please check all current or past medical conditions:',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Gastroparesis (Paralysis of your intestines)',
          key: 'gastroparesis'
        },
        {
          value: 'Triglycerides over 600 at any point',
          key: 'triglycerides_over_600'
        },
        {
          value: 'Pancreatic cancer or pancreatitis',
          key: 'pancreatic_cancer_or_pancreatitis'
        },
        {
          value: 'Type 1 Diabetes/Insulin-dependent diabetes',
          key: 'type1_diabetes'
        },
        { value: 'Hypoglycemia (low blood sugar)', key: 'hypoglycemia' },
        {
          value: 'Personal or family history of medullary thyroid cancer',
          key: 'medullary_thyroid_cancer'
        },
        {
          value:
            'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome',
          key: 'men2_syndrome'
        },
        { value: 'Anorexia or bulimia', key: 'anorexia_or_bulimia' },
        { value: 'Liver failure/liver cirrhosis', key: 'liver_failure' },
        {
          value: 'Chronic Kidney Disease Stage 3b or greater',
          key: 'ckd_stage_3b'
        },
        {
          value: 'Syndrome of Inappropriate Antidiuretic hormone',
          key: 'siadh'
        },
        {
          value: 'Current symptomatic gallstones',
          key: 'current_symptomatic_gallstones'
        },
        {
          value: 'Current gallstones without symptoms',
          key: 'current_gallstones_without_symptoms'
        },
        {
          value: 'Past removal of your gallbladder',
          key: 'past_gallbladder_removal'
        },
        {
          value: 'Hypothyroidism, Hyperthyroidism, or Thyroid Issues',
          key: 'thyroid_issues'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'wlusAltOnly_consentGallstones',
    label: 'Gallstone Consent:',
    type: 'select',
    educationalSlide:
      'Use of GLP-1 medications, including semaglutide, tirzepatide, and similar products, has been associated with an increased risk of gallbladder or biliary issues, including the development or worsening of gallstones. If you have existing but asymptomatic gallstones, treatment may increase the chance that these stones become symptomatic, potentially leading to pain, inflammation, or the need for medical or surgical treatment. Please notify your provider if you have a history of gallstones or experience new upper abdominal pain, nausea, or vomiting during therapy. Knowing this risk, do you wish to continue?',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I acknowledge that I have read and understood the above information and wish to continue.',
          key: 'consent'
        },
        {
          value:
            'I have read the above information and I do not wish to continue',
          key: 'non_consent'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_consentCholecystectomy',
    label: 'Cholecystectomy Consent:',
    type: 'select',
    educationalSlide:
      'You reported a history of gallbladder disease or gallbladder removal. This medication may still be appropriate, but it can affect how your body digests fats and bile. Without a gallbladder, bile flow and fat digestion are altered, which may increase the risk of side effects like stomach pain or diarrhea.\n\nThis medication may also impact absorption of certain nutrients (especially vitamins A, D, E, and K). To reduce risk, eat smaller, frequent meals, avoid processed foods, and consider a daily multivitamin unless otherwise directed by your provider.\n\nIf you have gallstones, please note that weight loss and this medication can increase the risk of gallstone formation or blockage, which can cause severe pain, infection, or pancreatitis. Seek immediate medical care for abdominal pain, fever, or vomiting.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I acknowledge that I have read and understood the above information and wish to continue.',
          key: 'consent'
        },
        {
          value:
            'I have read the above information and I do not wish to continue',
          key: 'non_consent'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_gastricBypass6Months',
    label: 'Have you had a gastric bypass in the past 6 months?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_allergicToGlpList',
    label: 'Are you allergic to any of the following?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Ozempic (Semaglutide)', key: 'ozempic' },
        { value: 'Mounjaro (Tirzepatide)', key: 'mounjaro' },
        { value: 'Wegovy (Semaglutide)', key: 'wegovy' },
        { value: 'Zepbound (Tirzepatide)', key: 'zepbound' },
        { value: 'Saxenda (Liraglutide)', key: 'saxenda' },
        { value: 'Trulicity (dulaglutide)', key: 'trulicity' },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'wlusAltOnly_diabetesMedications',
    label: 'Do you take any of the following medications?',
    type: 'select',
    educationalSlide: `
- Insulin
- Glimepiride (Amaryl)
- Meglitinides such as repaglinide or nateglinide
- Glipizide (Glucotrol and Glucotrol XL)
- Glyburide (Micronase, Glynase, and Diabeta)
- Sitagliptin
- Saxagliptin
- Linagliptin
- Alogliptin
    `,
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_currentOrPastTwoMonthsMedication',
    label:
      'Are you currently, or have you in the past two months, taken any of the following medications?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Semaglutide (Ozempic, Wegovy, Rybelsus)',
          key: 'semaglutide'
        },
        {
          value: 'Tirzepatide (Zepbound, Mounjaro)',
          key: 'tirzepatide'
        },
        // Hide for now, when get client we can use
        // {
        //   value: 'Liraglutide (Saxenda) or dulaglutide (Trulicity)',
        //   key: 'liraglutide'
        // },

        { value: 'None of these', key: 'none' }
      ]
    }
  },
  // New patient: injection ability
  {
    field: 'wlusAltOnly_ableToInject',
    label:
      'Are you able to safely give yourself injections or have someone help you?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "No, I'd need an oral option instead",
          key: 'no_oral_instead'
        },
        {
          value: 'Yes, I can inject myself or have reliable help',
          key: 'yes'
        }
      ]
    }
  },
  // Patients already on treatment
  {
    field: 'wlusAltOnly_lastTakenWhen',
    label: 'When did you last take your prescribed weight loss medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Within the past week', key: 'within_past_week' },
        { value: 'Within the past 2-3 weeks', key: 'within_2_3_weeks' },
        { value: 'It has been over a month', key: 'over_a_month' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_doseDecreaseConsent',
    label: 'Dose decrease consent',
    type: 'select',
    educationalSlide:
      "I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at a lower dose, typically about 50% of your previous dose, and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?",
    answer: {
      type: 'single_select',
      options: [
        { value: 'I understand and agree to the dose reduction', key: 'agree' },
        {
          value: 'I do not agree and do not wish to continue',
          key: 'disagree'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_restartMedicationConsent',
    label: 'Restart medication consent',
    type: 'select',
    educationalSlide:
      "I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at the starter dose and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?",
    answer: {
      type: 'single_select',
      options: [
        { value: 'I understand and agree to the dose reduction', key: 'agree' },
        {
          value: 'I do not agree and do not wish to continue',
          key: 'disagree'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_lastUseDate',
    label:
      'Please provide the approximate date (month and day) of your last use of this medication',
    type: 'info',
    answer: {
      type: 'text',
      name: 'wlusAltOnly_lastUseDate'
    }
  },
  {
    field: 'wlusAltOnly_mostRecentDose',
    label: `Accurate dosing is important for your safety. Select the exact medication and most recent milligram (mg) dose you administered. This may not be accurately displayed on your medication label and could be within your prescribers message to you. Do not estimate. The vial concentration (displayed mg/ml) or the volume you inject (units or ml) you inject is not the same as your prescribed dose (which is reflected as mg).
If you are unsure, select “I don’t know.” You will be asked to enter the prescription details or upload a clear photo so your care team can verify this for you. 
By selecting a dose, you confirm that it accurately reflects your medication and most recent administered dose. You may provide additional information at the end of the intake form or message your provider directly to provide further information.  (Select one)
`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Semaglutide/Ozempic/Wegovy 0.25mg',
          key: 'semaglutide_0_25'
        },
        {
          value: 'Semaglutide/Ozempic/Wegovy 0.5mg',
          key: 'semaglutide_0_5'
        },
        { value: 'Semaglutide/Ozempic/Wegovy 1mg', key: 'semaglutide_1' },
        {
          value: 'Semaglutide/Ozempic/Wegovy 1.5 or 1.7mg',
          key: 'semaglutide_1_5_or_1_7'
        },
        { value: 'Semaglutide/Ozempic/Wegovy 2.0mg', key: 'semaglutide_2' },
        { value: 'Semaglutide/Ozempic/Wegovy 2.4mg', key: 'semaglutide_2_4' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 2.5mg',
          key: 'tirzepatide_2_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 5mg', key: 'tirzepatide_5' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 7.5mg',
          key: 'tirzepatide_7_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 10mg', key: 'tirzepatide_10' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 12.5mg',
          key: 'tirzepatide_12_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 15mg', key: 'tirzepatide_15' },
        { value: "I don't see my dose", key: 'dont_see_my_dose' },
        { value: "I don't know my dose", key: 'dont_know_my_dose' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_currentDoseFreeText',
    label: 'Please provide us with your current dose',
    type: 'info',
    answer: {
      type: 'text',
      name: 'wlusAltOnly_currentDoseFreeText'
    }
  },
  {
    field: 'wlusAltOnly_brandOrCompounded',
    label:
      'Are you currently using a brand name medication (like Wegovy or Zepbound) or a compounded version  (like semaglutide or tirzepatide)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: "I'm taking a brand name medication", key: 'brand_name' },
        { value: "I'm taking a compounded product", key: 'compounded' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_compoundedMedicationDetails',
    label:
      'Please tell us the name and dose of the compounded medication you’re taking, as dose conversions can be confusing and we’ll need to complete an extra verification step to confirm accuracy. Concentration - look on your prescription label or vial for something that looks like "20 mg/mL" or "2.5 mg/mL." There may be several numbers which should all be provided. This shows how strong your medication is. Amount you take - write how much you administer, in units or milliliters (mL).',
    type: 'info',
    answer: {
      type: 'text',
      rows: 6,
      name: 'wlusAltOnly_compoundedMedicationDetails'
    }
  },
  {
    field: 'wlusAltOnly_doseVerificationPhotoUpload',
    label: `Here's how to share the information clearly to help your provider determine your dose.

1. Medication name – write the exact name from your prescription label including any additives such as glycine, vitamin B, etc.
2. Concentration – look on your prescription label or vial for something that looks like "20 mg/mL" or "2.5 mg/mL." There may be several numbers which should all be provided. This shows how strong your medication is. Please provide all the numbers listed.
3. Amount you took – write how much you injected or used, in units or milliliters (mL).
(Example: "Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.")
4. If you're unsure about your prescription details, please upload a clear photo of your medication label. Make sure the entire label is visible, and let us know how many units or milliliters (mL) you are currently using. You can upload the photo directly in your message thread.`,
    type: 'file_upload',
    accepts: IMAGE_TYPES,
    answer: [
      {
        resourceId: 'wlusAltOnly_doseVerificationPhotoUpload',
        buttonLabel: 'Upload Image'
      }
    ]
  },
  {
    field: 'wlusAltOnly_sideEffectsAdditives',
    label:
      'Have you experienced any of the following side effects from your current medication? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'Yes I have experienced fatigue and WOULD like to explore the option of including an additive which may help manage my fatigue symptoms',
          key: 'fatigue'
        },
        {
          value:
            'Yes I have experienced nausea and WOULD like to explore the option of including an additive which may help reduce your nausea',
          key: 'nausea'
        },
        {
          value:
            'Yes I have experienced muscle loss and WOULD like to explore the option of including an additive which may help to support muscle health',
          key: 'muscle_wasting'
        },
        {
          value: 'No (This does not direct to an additive recommendation)',
          key: 'no',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_howRespondingToMedication',
    label: 'How are you responding to your medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "I'm not feeling any effects and have no side effects",
          key: 'no_effects_no_side_effects'
        },
        {
          value: "I'm not feeling any effects but I'm having side effects",
          key: 'no_effects_has_side_effects'
        },
        {
          value: "I'm feeling the effects but have side effects",
          key: 'has_effects_has_side_effects'
        },
        {
          value: "I'm feeling the effects and have no side effects",
          key: 'has_effects_no_side_effects'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_howContinueTreatment',
    label: 'How would you like to continue your treatment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Stay at the same or closest equivalent dose',
          key: 'same_dose'
        },
        {
          value:
            'Increase the dose if possible, or continue current if already at maximum',
          key: 'increase_dose'
        },
        { value: 'Decrease my dose', key: 'decrease_dose' }
      ]
    }
  },
  {
    field: 'wlusAltOnly_photoOfPrescription',
    label:
      "Do you have a photo of your current prescription? We need this image to verify your current dosage. If we're unable to confirm your prescription, we can only offer the starting dose.",
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes I have this information', key: 'yes' },
        {
          value: "I don't have this information and I'm okay restarting",
          key: 'no_ok_restarting'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_prescriptionImageUpload',
    label:
      'Please upload a clear photo of your current prescription or bottle.\nMake sure the full label is visible, including your name, medication concentration, and usage instructions.',
    type: 'file_upload',
    accepts: IMAGE_TYPES,
    answer: [
      {
        resourceId: 'wlusAltOnly_prescriptionImageUpload',
        buttonLabel: 'Upload Image'
      }
    ]
  },
  {
    field: 'wlusAltOnly_otherInfoForDoctor',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'wlusAltOnly_otherInfoForDoctor'
    }
  },
  {
    field: 'wlusAltOnly_consentTruthfulness',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form.  I confirm that I have reviewed and understood all the questions asked of me.  I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor.  I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          key: 'consent'
        },
        {
          value:
            'I have read the above information and I do not wish to continue',
          key: 'non_consent'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_consentCompoundedGlp',
    label:
      'Informed Consent for Compounded GLP-1 and GIP/GLP-1 Receptor Agonist Therapy',
    type: 'select',
    educationalSlide: `You are requesting treatment with a compounded GLP-1 receptor agonist or GIP/GLP-1 receptor agonist medication, such as semaglutide, tirzepatide, or liraglutide, as part of a medical weight management or obesity treatment plan. These medications work by mimicking incretin hormones that regulate appetite, slow gastric emptying, promote fullness, and improve metabolic health.
\nCompounded medications are custom-prepared by licensed pharmacists. These formulations may allow for alternative dosing schedules, titration strategies, dosing frequencies, routes of administration, or the addition of ingredients intended to improve tolerability or reduce side effects. Compounded medications are not reviewed or approved by the U.S. Food and Drug Administration (FDA) and are not subject to the same testing, clinical trials, or manufacturing oversight as FDA-approved brand-name products. As a result, compounded medications may differ from FDA-approved products in consistency, potency, efficacy, and safety.
\nWeight loss medications are typically prescribed for individuals who are obese, or for individuals who are overweight with related medical conditions. Use of these medications in individuals who are overweight without an associated medical condition is considered off-label, meaning the medication is prescribed in a manner not specifically approved by the FDA. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.
\nPotential benefits of treatment may include weight loss or weight management, improved blood glucose control, reduced cardiovascular risk, and overall improvement in metabolic health. While many patients tolerate these medications well, they may cause side effects and, in rare cases, serious adverse events including emergency room visits, hospitalization, or death. Common side effects include nausea, vomiting, diarrhea, constipation, decreased appetite, and indigestion. Serious or less common risks include pancreatitis, low blood sugar, gallbladder disease, kidney injury or worsening kidney function, allergic reactions, and gastrointestinal motility disorders such as gastroparesis.
\nThese medications are not safe for use during pregnancy or while breastfeeding, as they may pose risks to a developing baby. The FDA advises the use of additional contraception because these medications may reduce the effectiveness of oral contraceptives. If you use oral birth control, you must continue it with a barrier method, such as condoms, for the first month after starting treatment and for one month after any dose increase. You may choose to switch to a non-oral contraceptive method, such as an intrauterine device or implant, prior to starting treatment. After stopping the medication, you should continue using effective contraception for at least two months to ensure the medication has fully cleared from your system before attempting to conceive.
\nOngoing follow-up is required to monitor your response to treatment and to assess for side effects. Medication doses may be adjusted based on tolerability and clinical response.
\nBy proceeding with treatment, you acknowledge that compounded medications are not FDA-approved and may differ from FDA-approved products in safety and effectiveness. You understand the potential benefits, risks, side effects, and pregnancy-related precautions associated with GLP-1 and GIP/GLP-1 receptor agonist therapy, including possible off-label use, and agree to follow prescribed instructions, attend required follow-up, and promptly report any concerns or side effects.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read and understand the above information and I wish to continue',
          key: 'consent'
        },
        {
          value:
            'I have read the above information and I do not wish to continue',
          key: 'non_consent'
        }
      ]
    }
  },
  {
    field: 'wlusAltOnly_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    field: 'wlusAltOnly_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    blockQuestionFlow: true,
    type: 'info',
    isLast: true
  }
] as const;

export type wlusAltOnlyAnswerField = Partial<
  QuestionAnswerMap<typeof wlusAltOnlyQuestions>
>;

export default wlusAltOnlyQuestions;
